import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8002/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Skin Rankings API
export const skinAPI = {
  getRankings: (category = 'all', limit = 100) =>
    api.get(`/skins/rankings?category=${category}&limit=${limit}`),
  
  getCategories: () =>
    api.get('/skins/categories'),
  
  getRecentChanges: () =>
    api.get('/skins/changes'),

  resolveSkinImage: (weapon, skinName, demoSkinId = null) => {
    const params = new URLSearchParams({
      weapon: weapon,
      skin: skinName
    });
    if (demoSkinId) params.append('demo_skin_id', demoSkinId);
    return api.get(`/skins/resolve-image?${params.toString()}`);
  }
};

// Skin Rankings API
export const skinRankingsAPI = {
  getTopSkins: (limit = 100, category = null) => {
    const params = new URLSearchParams({ limit: limit.toString() });
    if (category) params.append('category', category);
    return api.get(`/skins/rankings/top?${params.toString()}`);
  },

  getTopSkinsByCategories: (limitPerCategory = 20) =>
    api.get(`/skins/rankings/categories?limit_per_category=${limitPerCategory}`),

  searchSkins: (query, limit = 1000) =>
    api.get(`/skins/rankings/search?q=${encodeURIComponent(query)}&limit=${limit}`),

  getRankingStatistics: () =>
    api.get('/skins/rankings/stats'),

  getCategories: () =>
    api.get('/skins/rankings/categories/list'),

  getSkinDetails: (id) =>
    api.get(`/skins/${id}`),

  getSkinUsageDetails: (id, page = 1, limit = 25) =>
    api.get(`/skins/${id}/usage?page=${page}&limit=${limit}`),

  getSkinRankingHistory: (id, period = '1m') =>
    api.get(`/skins/${id}/history?period=${period}`),

  getRankingPositionChartData: (id, period = '1m') =>
    api.get(`/skins/${id}/chart/rankings?period=${period}`),

  getScoreChangesChartData: (id, period = '1m') =>
    api.get(`/skins/${id}/chart/scores?period=${period}`),

  getTopTeamsUsingSkin: (id, limit = 5) =>
    api.get(`/skins/${id}/teams?limit=${limit}`),

  // Admin decay endpoint
  decaySkinRankings: (decayRate = 0.1, dryRun = false) =>
    api.post('/admin/crawler/decay-skin-rankings', {
      decay_rate: decayRate,
      dry_run: dryRun
    })
};

// Admin API
export const adminAPI = {
  login: (password) =>
    api.post('/admin/login', { password }),
  
  getDemos: () =>
    api.get('/admin/demos'),
  
  getDemoSkins: (demoId) =>
    api.get(`/admin/crawler/demo-skins/${demoId}`),
  
  getStats: () =>
    api.get('/admin/stats'),

  // HLTV Admin API
  getHltvStats: () =>
    api.get('/admin/hltv/stats'),
    
  getHltvTeams: (page = 1, limit = 1000) =>
    api.get(`/admin/hltv/teams?page=${page}&limit=${limit}`),
    
  getHltvPlayers: (page = 1, limit = 1000, search = '', country = '') =>
    api.get(`/admin/hltv/players?page=${page}&limit=${limit}&search=${search}&country=${country}`),
    
  getHltvRankings: (page = 1, limit = 1000, date = '') =>
    api.get(`/admin/hltv/rankings?page=${page}&limit=${limit}&date=${date}`),

      getHltvMatches: (page = 1, limit = 100) =>
        api.get(`/admin/hltv/matches?page=${page}&limit=${limit}`),

      // Crawler & Demo Processing APIs
      getCrawlerStatus: () =>
        api.get('/admin/crawler/status'),

      getDemoFiles: (page = 1, limit = 20, status = '', matchId = '') =>
        api.get(`/admin/crawler/demo-files?page=${page}&limit=${limit}&status=${status}&match_id=${matchId}`),


      runCrawlerCommand: (endpoint, payload = {}) =>
        api.post(`/admin/crawler/${endpoint}`, payload),

      // Skin database management
      getSkins: (page = 1, limit = 25, search = '', weapon = '', category = '') => {
        const params = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString()
        });
        if (search) params.append('search', search);
        if (weapon) params.append('weapon', weapon);
        if (category) params.append('category', category);
        
        return api.get(`/admin/skins?${params}`);
      },

      getSkinStats: () =>
        api.get('/admin/skins/stats'),

      getSkinFilters: () =>
        api.get('/admin/skins/filters'),

      refreshSkins: (clearExisting = false, limit = null) =>
        api.post('/admin/skins/refresh', {
          clear: clearExisting,
          limit: limit
        }),

  getSkinDetail: (skinId) =>
    api.get(`/admin/skins/${skinId}`),

  resolveSkinImage: (weapon, skinName, demoSkinId = null) => {
    const params = new URLSearchParams({
      weapon: weapon,
      skin: skinName
    });
    if (demoSkinId) params.append('demo_skin_id', demoSkinId);
    return api.get(`/skins/resolve-image?${params.toString()}`);
  },

  // Skin mapping management
  getUnmappedSkins: (page = 1, limit = 250) =>
    api.get(`/admin/skins/mappings/unmapped?page=${page}&limit=${limit}`),

  getDemoSkinsForMapping: (page = 1, limit = 250, showMapped = false) =>
    api.get(`/admin/skins/mappings/demo-skins?page=${page}&limit=${limit}&show_mapped=${showMapped}`),

  saveSkinMapping: (demoSkinName, demoWeaponName, mappedSkinId, createdBy = 'admin') =>
    api.post('/admin/skins/mappings', {
      demo_skin_name: demoSkinName,
      demo_weapon_name: demoWeaponName,
      mapped_skin_id: mappedSkinId,
      created_by: createdBy
    }),

  getMappingStats: () =>
    api.get('/admin/skins/mappings/stats'),

  searchSkins: (query, weapon = '', limit = 1000) =>
    api.get(`/admin/skins/search?q=${encodeURIComponent(query)}&weapon=${encodeURIComponent(weapon)}&limit=1000`), //${limit}

  // Reverse mapping management (database skins -> demo skins)
  getUnmappedDatabaseSkins: (page = 1, limit = 25) =>
    api.get(`/admin/skins/reverse-mappings/unmapped?page=${page}&limit=${limit}`),

  getAvailableDemoSkins: (query = '', weapon = '') =>
    api.get(`/admin/skins/reverse-mappings/demo-skins?q=${encodeURIComponent(query)}&weapon=${encodeURIComponent(weapon)}`),

  getReverseMappingStats: () =>
    api.get('/admin/skins/reverse-mappings/stats'),

  updateSkinDisplayName: (skinId, displayName) =>
    api.post(`/admin/skins/${skinId}/display-name`, {
      display_name: displayName
    }),
};

export default api;
